import EventEmitter from "EventEmitter";
import * as THREE from "three";
import { Projector } from "three/examples/jsm/renderers/Projector";

import HotSpot from "./hotspot";
import hotspotsData from "../../data/hotspots.json";
import hopspotDotTexture from "../../assets/textures/hotspot-dot.png";
import hopspotRingTexture from "../../assets/textures/hotspot-ring.png";

export default class HotspotsManager extends EventEmitter {
  constructor(scene, renderer, camera, contentEl) {
    super();

    this.debug = false;
    this.scene = scene;
    this.renderer = renderer;
    this.camera = camera;
    this.webglContent = contentEl;
    this.mouse = { x: 0, y: 0 };

    this.hotSpots = [];
    this.colliders = [];
    this.raycaster = new THREE.Raycaster();

    this.webglContent.addEventListener("mousemove", this._onMouseMove.bind(this));
    this.webglContent.addEventListener("mouseup", this._onMouseUp.bind(this));
  }

  createHotSpots() {
    const loader = new THREE.TextureLoader();
    const texture1 = loader.load(hopspotDotTexture);
    const texture2 = loader.load(hopspotRingTexture);

    const matCircle = new THREE.MeshBasicMaterial({
      map: texture1,
      transparent: true, // so we can see the ground
      depthWrite: true, // so we don't have to sort
      side: THREE.DoubleSide,
    });

    const matRing = new THREE.MeshBasicMaterial({
      map: texture2,
      transparent: true, // so we can see the ground
      depthWrite: true, // so we don't have to sort
      side: THREE.DoubleSide,
    });

    hotspotsData.forEach((h) => {
      const hotSpot = new HotSpot(
        new THREE.Vector3(h.x, h.y, h.z),
        matCircle,
        matRing
      );
      hotSpot.data = h;
      this.hotSpots.push(hotSpot);

      this.scene.add(hotSpot.Particle);
      this.colliders.push(hotSpot.Ring);

      if (h.category === "") {
        hotSpot.open();
      } else {
        hotSpot.close(0);
      }
    });
  };

  updateHotSpots = (category, parts = []) => {

    this.debug && console.log('Hotspot category:', category);
    this.hotSpots.forEach((h) => {
      if (category && category !== "" && h.data.category.toLowerCase().includes(category.toLowerCase())) 
      {
        if (h.data.partConstrain.length === 0) {
          h.open();

        } else {
          let contain = false;

          h.data.partConstrain.forEach(c => {
            if (parts.includes(c)) {
              contain = true;
            }
          });

          if (contain) {
            h.open();
          } else {
            h.close();
          }
        }
      } else if ((category === "" || !category)  &&  h.data.category === "") {
        h.open();
      } else {
        h.close();
      }
    });
  }

  _onMouseMove(event) {
    this.mouse.x = event.clientX;
    this.mouse.y = event.clientY;

    const collisions = this._checkCollision();

    if (!collisions.length) {
      return this.hotSpots.forEach((hotspot) => {
        if (hotspot.IsOver) {
          hotspot.mouseOut();
          this.emit("hotspotOut");  
        }
      });
    }

    const object = collisions[0].object;
    const hotspot = this._getHotspotFromCollider(object);
    const screenPosition = this._getScreenPosition(object);
    const data = hotspot.data;
    data.screenPosition = screenPosition;

    hotspot.mouseOver();
    this.emit("hotspotOver", data);  
  };

  _onMouseUp() {
    const collisions = this._checkCollision();

    if (!collisions.length) {
      return;
    }

    const object = collisions[0].object;
    const screenPosition = this._getScreenPosition(object);
    const data = this._getHotspotFromCollider(object).data;
    data.screenPosition = screenPosition;
    
    this.emit("hotspotSelected", data);

    setTimeout(() => {
      const screenPosition = this._getScreenPosition(object);
      data.screenPosition = screenPosition;
      this.emit("hotspotSelectedDelayed", data);
    }, 1000);
  };

  _checkCollision() {
    const mouse = new THREE.Vector2();
    mouse.x = (this.mouse.x / this.webglContent.offsetWidth) * 2 - 1;
    mouse.y = -(this.mouse.y / this.webglContent.offsetHeight) * 2 + 1;

    this.raycaster.setFromCamera(mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.colliders);

    return intersects;
  };

  _getHotspotFromCollider(col) {
    const len = this.hotSpots.length;

    for (let i = 0; i < len; i++) {
      if (this.hotSpots[i].Ring === col) return this.hotSpots[i];
    }

    return null;
  };

  _getScreenPosition(object) {
    const width = this.webglContent.offsetWidth,
      height = this.webglContent.offsetHeight;
    const widthHalf = width / 2,
      heightHalf = height / 2;

    const vector = new THREE.Vector3();
    const projector = new Projector();
    projector.projectVector(
      vector.setFromMatrixPosition(object.matrixWorld),
      this.camera
    );

    vector.x = vector.x * widthHalf + widthHalf;
    vector.y = -(vector.y * heightHalf) + heightHalf;

    return vector;
  }

  // RENDER UPDATE

  update = () => {
    this.hotSpots.forEach((hotspot) => {
      hotspot.updateCameraPosition(this.camera.position);
    });
  };
}
